import React, { useState } from 'react';
import { ContentEditorProps } from './types';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PlusCircle, X } from 'lucide-react';

/**
 * Editor for ad group content type
 */
export const AdGroupEditor: React.FC<ContentEditorProps> = ({ 
  content, 
  onSave, 
  onCancel 
}) => {
  // Extract ad group specific data
  const { 
    name: adGroupName, 
    platform = 'google',
    ads = [], 
    settings = {},
    targeting = {},
    // Instagram/social media specific fields
    descriptions = [],
    short_headlines = [],
    bonus_metadata = {}
  } = content.content_json;

  // Determine platform from content name or other indicators
  const determinePlatform = () => {
    if (content.name?.toLowerCase().includes('instagram')) return 'instagram';
    if (content.name?.toLowerCase().includes('facebook')) return 'facebook';
    if (content.name?.toLowerCase().includes('google')) return 'google';
    if (content.name?.toLowerCase().includes('linkedin')) return 'linkedin';
    return platform;
  };

  const actualPlatform = determinePlatform();
  
  // Set up state for form
  const [formState, setFormState] = useState({
    name: adGroupName || content.name || '',
    platform: actualPlatform || 'google',
    ads: [...ads],
    settings: { ...settings },
    targeting: { ...targeting },
    descriptions: [...descriptions],
    short_headlines: [...short_headlines],
    bonus_metadata: { ...bonus_metadata }
  });
  
  // Handle basic input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle platform change
  const handlePlatformChange = (value: string) => {
    setFormState(prev => ({ ...prev, platform: value }));
  };
  
  // Handle adding a new ad
  const handleAddAd = () => {
    let newAd: any = {};
    
    switch (formState.platform) {
      case 'google':
        newAd = {
          id: `ad-${Date.now()}`,
          type: 'responsive',
          headlines: [
            { text: 'Primary Headline' },
            { text: 'Secondary Headline' },
            { text: 'Optional Headline' }
          ],
          descriptions: [
            { text: 'Primary description text goes here.' },
            { text: 'Secondary description adds more details.' }
          ],
          path: 'example.com/path',
          finalUrl: 'https://example.com/landing-page'
        };
        break;
        
      case 'facebook':
        newAd = {
          id: `ad-${Date.now()}`,
          type: 'image',
          headline: 'Ad Headline',
          body: 'Ad body text goes here. Explain your value proposition.',
          cta: 'Learn More',
          imageUrl: ''
        };
        break;
        
      default:
        newAd = {
          id: `ad-${Date.now()}`,
          type: 'standard',
          headline: 'New Ad',
          body: 'Ad content goes here'
        };
    }
    
    setFormState(prev => ({
      ...prev,
      ads: [...prev.ads, newAd]
    }));
  };
  
  // Handle removing an ad
  const handleRemoveAd = (adId: string) => {
    setFormState(prev => ({
      ...prev,
      ads: prev.ads.filter(ad => ad.id !== adId)
    }));
  };
  
  // Handle updating a Google ad
  const handleUpdateGoogleAd = (adIndex: number, field: string, subIndex: number, value: string) => {
    const updatedAds = [...formState.ads];
    const ad = { ...updatedAds[adIndex] };
    
    if (field === 'headlines' || field === 'descriptions') {
      const items = [...ad[field]];
      items[subIndex] = { ...items[subIndex], text: value };
      ad[field] = items;
    } else {
      ad[field] = value;
    }
    
    updatedAds[adIndex] = ad;
    setFormState(prev => ({ ...prev, ads: updatedAds }));
  };
  
  // Handle updating a Facebook ad
  const handleUpdateFacebookAd = (adIndex: number, field: string, value: string) => {
    const updatedAds = [...formState.ads];
    const ad = { ...updatedAds[adIndex] };
    
    ad[field] = value;
    
    updatedAds[adIndex] = ad;
    setFormState(prev => ({ ...prev, ads: updatedAds }));
  };

  // Handle updating Instagram headlines
  const handleUpdateHeadline = (index: number, value: string) => {
    const updatedHeadlines = [...formState.short_headlines];
    updatedHeadlines[index] = value;
    setFormState(prev => ({ ...prev, short_headlines: updatedHeadlines }));
  };

  // Handle adding a new headline
  const handleAddHeadline = () => {
    setFormState(prev => ({ 
      ...prev, 
      short_headlines: [...prev.short_headlines, ''] 
    }));
  };

  // Handle removing a headline
  const handleRemoveHeadline = (index: number) => {
    const updatedHeadlines = formState.short_headlines.filter((_, i) => i !== index);
    setFormState(prev => ({ ...prev, short_headlines: updatedHeadlines }));
  };

  // Handle updating Instagram descriptions
  const handleUpdateDescription = (index: number, value: string) => {
    const updatedDescriptions = [...formState.descriptions];
    updatedDescriptions[index] = value;
    setFormState(prev => ({ ...prev, descriptions: updatedDescriptions }));
  };

  // Handle adding a new description
  const handleAddDescription = () => {
    setFormState(prev => ({ 
      ...prev, 
      descriptions: [...prev.descriptions, ''] 
    }));
  };

  // Handle removing a description
  const handleRemoveDescription = (index: number) => {
    const updatedDescriptions = formState.descriptions.filter((_, i) => i !== index);
    setFormState(prev => ({ ...prev, descriptions: updatedDescriptions }));
  };

  // Handle updating metadata fields
  const handleUpdateMetadata = (field: string, value: any) => {
    setFormState(prev => ({
      ...prev,
      bonus_metadata: {
        ...prev.bonus_metadata,
        [field]: value
      }
    }));
  };
  
  // Render Google ad editor
  const renderGoogleAdEditor = (ad: any, index: number) => {
    return (
      <div key={ad.id} className="border p-4 rounded-md mb-4 bg-white dark:bg-gray-900">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium">Responsive Search Ad {index + 1}</h4>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => handleRemoveAd(ad.id)}
            className="text-red-500 hover:text-red-700"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Headlines</Label>
            <div className="space-y-2">
              {ad.headlines.map((headline: any, hIndex: number) => (
                <div key={hIndex} className="flex items-center gap-2">
                  <Input
                    value={headline.text}
                    onChange={(e) => handleUpdateGoogleAd(index, 'headlines', hIndex, e.target.value)}
                    placeholder={`Headline ${hIndex + 1}`}
                    className="flex-1"
                    maxLength={30}
                  />
                  <div className="text-xs text-muted-foreground w-12 text-right">
                    {headline.text.length}/30
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Descriptions</Label>
            <div className="space-y-2">
              {ad.descriptions.map((desc: any, dIndex: number) => (
                <div key={dIndex} className="flex items-center gap-2">
                  <Input
                    value={desc.text}
                    onChange={(e) => handleUpdateGoogleAd(index, 'descriptions', dIndex, e.target.value)}
                    placeholder={`Description ${dIndex + 1}`}
                    className="flex-1"
                    maxLength={90}
                  />
                  <div className="text-xs text-muted-foreground w-12 text-right">
                    {desc.text.length}/90
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`ad-${index}-path`}>Display Path</Label>
              <Input
                id={`ad-${index}-path`}
                value={ad.path || ''}
                onChange={(e) => handleUpdateGoogleAd(index, 'path', 0, e.target.value)}
                placeholder="example.com/path"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor={`ad-${index}-finalUrl`}>Final URL</Label>
              <Input
                id={`ad-${index}-finalUrl`}
                value={ad.finalUrl || ''}
                onChange={(e) => handleUpdateGoogleAd(index, 'finalUrl', 0, e.target.value)}
                placeholder="https://example.com/landing-page"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Render Facebook ad editor
  const renderFacebookAdEditor = (ad: any, index: number) => {
    return (
      <div key={ad.id} className="border p-4 rounded-md mb-4 bg-white dark:bg-gray-900">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium">Facebook Ad {index + 1}</h4>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => handleRemoveAd(ad.id)}
            className="text-red-500 hover:text-red-700"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor={`ad-${index}-headline`}>Headline</Label>
            <div className="flex items-center gap-2">
              <Input
                id={`ad-${index}-headline`}
                value={ad.headline || ''}
                onChange={(e) => handleUpdateFacebookAd(index, 'headline', e.target.value)}
                placeholder="Headline"
                className="flex-1"
                maxLength={40}
              />
              <div className="text-xs text-muted-foreground w-12 text-right">
                {(ad.headline || '').length}/40
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor={`ad-${index}-body`}>Body Text</Label>
            <div className="flex items-start gap-2">
              <Textarea
                id={`ad-${index}-body`}
                value={ad.body || ''}
                onChange={(e) => handleUpdateFacebookAd(index, 'body', e.target.value)}
                placeholder="Ad body text"
                className="flex-1 min-h-[80px]"
                maxLength={125}
              />
              <div className="text-xs text-muted-foreground w-12 text-right pt-2">
                {(ad.body || '').length}/125
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`ad-${index}-cta`}>Call to Action</Label>
              <Select 
                value={ad.cta || 'Learn More'} 
                onValueChange={(value) => handleUpdateFacebookAd(index, 'cta', value)}
              >
                <SelectTrigger id={`ad-${index}-cta`}>
                  <SelectValue placeholder="Select CTA" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Learn More">Learn More</SelectItem>
                  <SelectItem value="Shop Now">Shop Now</SelectItem>
                  <SelectItem value="Sign Up">Sign Up</SelectItem>
                  <SelectItem value="Download">Download</SelectItem>
                  <SelectItem value="Get Offer">Get Offer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor={`ad-${index}-imageUrl`}>Image URL</Label>
              <Input
                id={`ad-${index}-imageUrl`}
                value={ad.imageUrl || ''}
                onChange={(e) => handleUpdateFacebookAd(index, 'imageUrl', e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
            </div>
          </div>
          
          {ad.imageUrl && (
            <div className="mt-2 border rounded overflow-hidden h-40 bg-gray-100 dark:bg-gray-800">
              <img 
                src={ad.imageUrl} 
                alt="Ad preview" 
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://placehold.co/600x400?text=Invalid+Image+URL';
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  };
  
  // Render Instagram editor
  const renderInstagramEditor = () => {
    return (
      <div className="space-y-6">
        {/* Headlines Section */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium">Short Headlines</h3>
              <p className="text-sm text-muted-foreground">
                Create impactful headlines for your Instagram ads (max 40 characters)
              </p>
            </div>
            <Button 
              type="button"
              onClick={handleAddHeadline}
              size="sm"
              variant="outline"
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              Add Headline
            </Button>
          </div>
          
          <div className="space-y-2">
            {formState.short_headlines.map((headline, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  value={headline}
                  onChange={(e) => handleUpdateHeadline(index, e.target.value)}
                  placeholder={`Headline ${index + 1}`}
                  className="flex-1"
                  maxLength={40}
                />
                <div className="text-xs text-muted-foreground w-12 text-right">
                  {headline.length}/40
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveHeadline(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {formState.short_headlines.length === 0 && (
              <div className="text-center py-4 text-muted-foreground">
                No headlines yet. Click "Add Headline" to create one.
              </div>
            )}
          </div>
        </div>

        {/* Descriptions Section */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium">Descriptions</h3>
              <p className="text-sm text-muted-foreground">
                Write compelling descriptions for your ads
              </p>
            </div>
            <Button 
              type="button"
              onClick={handleAddDescription}
              size="sm"
              variant="outline"
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              Add Description
            </Button>
          </div>
          
          <div className="space-y-3">
            {formState.descriptions.map((description, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-start gap-2">
                  <Textarea
                    value={description}
                    onChange={(e) => handleUpdateDescription(index, e.target.value)}
                    placeholder={`Description ${index + 1}`}
                    className="flex-1 min-h-[80px]"
                    maxLength={300}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveDescription(index)}
                    className="text-red-500 hover:text-red-700 mt-2"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="text-xs text-muted-foreground text-right">
                  {description.length}/300 characters
                </div>
              </div>
            ))}
            {formState.descriptions.length === 0 && (
              <div className="text-center py-4 text-muted-foreground">
                No descriptions yet. Click "Add Description" to create one.
              </div>
            )}
          </div>
        </div>

        {/* Metadata Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Additional Metadata</h3>
          
          {/* CTA Suggestions */}
          <div className="space-y-2">
            <Label>CTA Suggestions</Label>
            <Input
              value={formState.bonus_metadata?.cta_suggestions?.[0] || ''}
              onChange={(e) => handleUpdateMetadata('cta_suggestions', [e.target.value])}
              placeholder="e.g., Start Your Adventure Now"
            />
          </div>

          {/* Keywords */}
          <div className="space-y-2">
            <Label>Search Keywords (comma-separated)</Label>
            <Textarea
              value={formState.bonus_metadata?.search_keywords?.join(', ') || ''}
              onChange={(e) => handleUpdateMetadata('search_keywords', 
                e.target.value.split(',').map(k => k.trim()).filter(k => k)
              )}
              placeholder="e.g., travel videos, adventure, cultural insights"
              className="min-h-[60px]"
            />
          </div>

          {/* Negative Keywords */}
          <div className="space-y-2">
            <Label>Negative Keywords (comma-separated)</Label>
            <Textarea
              value={formState.bonus_metadata?.negative_keywords?.join(', ') || ''}
              onChange={(e) => handleUpdateMetadata('negative_keywords', 
                e.target.value.split(',').map(k => k.trim()).filter(k => k)
              )}
              placeholder="e.g., cheap, discount, free"
              className="min-h-[60px]"
            />
          </div>

          {/* Audience Targeting */}
          <div className="space-y-2">
            <Label>Audience Targeting (comma-separated)</Label>
            <Textarea
              value={formState.bonus_metadata?.audience_targeting_suggestions?.join(', ') || ''}
              onChange={(e) => handleUpdateMetadata('audience_targeting_suggestions', 
                e.target.value.split(',').map(k => k.trim()).filter(k => k)
              )}
              placeholder="e.g., travel lovers, adventure seekers, cultural explorers"
              className="min-h-[60px]"
            />
          </div>
        </div>
      </div>
    );
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Create updated content object based on platform
    let contentJson: any = {
      ...content.content_json,
      name: formState.name
    };

    if (actualPlatform === 'instagram') {
      // For Instagram, save the specific fields
      contentJson = {
        ...contentJson,
        descriptions: formState.descriptions,
        short_headlines: formState.short_headlines,
        bonus_metadata: formState.bonus_metadata
      };
    } else {
      // For other platforms, save the standard fields
      contentJson = {
        ...contentJson,
        platform: formState.platform,
        ads: formState.ads,
        settings: formState.settings,
        targeting: formState.targeting
      };
    }

    const updatedContent: Partial<typeof content> = {
      content_json: contentJson,
      name: formState.name // Update the content name to match the ad group name
    };
    
    // Save changes
    await onSave(updatedContent);
  };
  
  return (
    <div className="w-full h-full overflow-auto bg-white dark:bg-gray-900 p-6">
      <form onSubmit={handleSubmit}>
        <Card className="w-full max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle>Edit Ad Group</CardTitle>
          </CardHeader>
          
          <CardContent>
            {actualPlatform === 'instagram' ? (
              // For Instagram, show the direct editor without tabs
              <div className="space-y-6">
                {/* Ad Group Name */}
                <div className="space-y-2">
                  <Label htmlFor="name">Ad Group Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formState.name}
                    onChange={handleChange}
                    placeholder="Enter ad group name"
                    required
                  />
                </div>
                
                {/* Instagram-specific editor */}
                {renderInstagramEditor()}
              </div>
            ) : (
              // For other platforms, show the tabs interface
              <Tabs defaultValue="basic">
                <TabsList className="mb-4">
                  <TabsTrigger value="basic">Basic Info</TabsTrigger>
                  <TabsTrigger value="ads">Ad Variations</TabsTrigger>
                  <TabsTrigger value="preview">Preview</TabsTrigger>
                </TabsList>
                
                <TabsContent value="basic" className="space-y-6">
                {/* Ad Group Name */}
                <div className="space-y-2">
                  <Label htmlFor="name">Ad Group Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formState.name}
                    onChange={handleChange}
                    placeholder="Enter ad group name"
                    required
                  />
                </div>
                
                {/* Platform */}
                <div className="space-y-2">
                  <Label htmlFor="platform">Ad Platform</Label>
                  <Select 
                    value={formState.platform} 
                    onValueChange={handlePlatformChange}
                  >
                    <SelectTrigger id="platform">
                      <SelectValue placeholder="Select platform" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="google">Google Ads</SelectItem>
                      <SelectItem value="facebook">Facebook Ads</SelectItem>
                      <SelectItem value="linkedin">LinkedIn Ads</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Add more basic settings as needed */}
                <div className="p-6 border rounded-md bg-gray-50 dark:bg-gray-800">
                  <p className="text-center text-muted-foreground">
                    Additional ad group settings such as budget, bidding strategy, 
                    and targeting are managed through the platform's dashboard.
                  </p>
                </div>
              </TabsContent>
              
              <TabsContent value="ads" className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium">Ad Variations</h3>
                  <Button 
                    type="button"
                    onClick={handleAddAd}
                    className="flex items-center"
                  >
                    <PlusCircle className="h-4 w-4 mr-2" /> 
                    Add Ad
                  </Button>
                </div>
                
                <div className="space-y-2">
                  {formState.ads.length === 0 ? (
                    <div className="p-8 border rounded-md text-center">
                      <p className="text-muted-foreground mb-4">No ads created yet</p>
                      <Button onClick={handleAddAd}>Create First Ad</Button>
                    </div>
                  ) : (
                    <div>
                      {formState.ads.map((ad, index) => (
                        formState.platform === 'google' 
                          ? renderGoogleAdEditor(ad, index)
                          : renderFacebookAdEditor(ad, index)
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="preview" className="space-y-6">
                {formState.ads.length === 0 ? (
                  <div className="p-8 border rounded-md text-center">
                    <p className="text-muted-foreground">
                      Create ads to see previews
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <h3 className="text-lg font-medium">Ad Previews</h3>
                    
                    {formState.platform === 'google' ? (
                      <div className="space-y-4">
                        {formState.ads.map((ad, index) => (
                          <div key={index} className="p-4 border rounded-md">
                            <div className="text-blue-600 text-sm mb-1">
                              Ad · {ad.path || 'example.com/path'}
                            </div>
                            <div className="text-[18px] text-blue-800 font-medium mb-1">
                              {ad.headlines && ad.headlines[0]?.text}
                              {ad.headlines && ad.headlines[1]?.text && ` | ${ad.headlines[1].text}`}
                            </div>
                            <div className="text-sm text-gray-600">
                              {ad.descriptions && ad.descriptions[0]?.text}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {formState.ads.map((ad, index) => (
                          <div key={index} className="p-4 border rounded-md">
                            {ad.imageUrl && (
                              <div className="w-full h-48 bg-gray-100 mb-3 rounded overflow-hidden">
                                <img 
                                  src={ad.imageUrl} 
                                  alt="Ad" 
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src = 'https://placehold.co/600x400?text=Image+Preview';
                                  }}
                                />
                              </div>
                            )}
                            <div className="text-sm text-blue-600 mb-1">
                              SPONSORED
                            </div>
                            <div className="font-bold mb-2">
                              {ad.headline || 'Ad Headline'}
                            </div>
                            <div className="text-sm mb-2">
                              {ad.body || 'Ad body text goes here.'}
                            </div>
                            <div className="bg-blue-600 text-white text-center py-1 px-3 text-sm rounded w-32">
                              {ad.cta || 'Learn More'}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>
            </Tabs>
            )}
          </CardContent>
          
          <CardFooter className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit">
              Save Changes
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
};

export default AdGroupEditor; 